import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "../components/layout/Layout";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import { Button } from "../components/common/Button";
// import { AdBanner } from "../components/ads/AdBanner";
import { Star, Calendar, Tag, PenSquare } from "lucide-react";
import { tmdbApi } from "../services/tmdb";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { toggleShowWatchlist } from "../store/slices/mediaSlice";
import { useQuery } from "@tanstack/react-query";
import { CastSection } from "../components/media/CastSection";
import VideoSection from "../components/media/VideoSection";
import { SeasonSection } from "../components/media/SeasonSection";
import { StreamingServices } from "../components/media/StreamingServices";
import {
  DetailContainer,
  BackdropImage,
  Content,
  PosterContainer,
  InfoContainer,
  Title,
  MetaInfo,
  MetaItem,
  Overview,
  ActionButtons,
  GenresContainer,
  GenreTag,
} from "../styles/MediaDetailStyles";
import { useTranslation } from 'react-i18next';
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";
import HighPerformanceBanner from '../components/ads/HighPerformanceBanner';
import HilltopAd from '../components/ads/HilltopAd';
import Crxcr2PopIn from '../components/ads/Crxcr2PopIn';
import { SEOHead } from "../components/seo/SEOHead";
import { buildSEOTitle } from "../utils/seo";
// Removed CrackRevenueAd in favor of Crxcr2PopIn

const TVShowDetail = () => {
  const { id: showId } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const watchlist = useSelector((state: RootState) => state.media.watchlist);
  const user = useSelector((state: RootState) => state.auth.user);
  const { t } = useTranslation();
  
  // Scroll automatique vers le haut au chargement
  useScrollToTopOnMount();

  const { data: showDetails, isLoading } = useQuery({
    queryKey: ["tv", showId],
    queryFn: () => tmdbApi.getDetails("tv", parseInt(showId || '0')),
    enabled: !!showId,
  });

  if (isLoading || !showDetails) return <LoadingSpinner />;

  const isInWatchlist = watchlist.shows.some((item) => item.id === showDetails.id);
  const isAdmin = user?.role === 'admin';
  const firstAirYear = new Date(showDetails.first_air_date).getFullYear();
  const currentUrl = window.location.href;
  const seoTitle = buildSEOTitle({
    baseTitle: showDetails.name,
    year: firstAirYear,
    prefix: 'Critique de',
    hook: 'Saisons, casting et avis',
  });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TVSeries",
    name: showDetails.name,
    description: showDetails.overview,
    image: `https://image.tmdb.org/t/p/w500${showDetails.poster_path}`,
    datePublished: showDetails.first_air_date,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: showDetails.vote_average,
      ratingCount: showDetails.vote_count,
      bestRating: "10",
      worstRating: "0",
    },
    genre: showDetails.genres?.map((g: { name: string }) => g.name),
  } as const;

  // Détection mobile locale pour forcer le fallback overlay du postitial
  const isMobileViewport = (() => {
    try { return !!window.matchMedia && window.matchMedia('(max-width: 768px)').matches; } catch { return window.innerWidth <= 768; }
  })();
  const isMobileUA = (() => {
    try { return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent); } catch { return false; }
  })();
  const forceMobileFallback = isMobileViewport || isMobileUA;

  const handleCreateBlogPost = () => {
    const blogData = {
      title: showDetails.name,
      type: 'tv',
      tmdbId: showDetails.id,
      year: firstAirYear,
      image: `https://image.tmdb.org/t/p/original${showDetails.backdrop_path}`,
      poster: `https://image.tmdb.org/t/p/w500${showDetails.poster_path}`,
      overview: showDetails.overview,
      rating: showDetails.vote_average,
      genres: showDetails.genres.map(g => g.name),
    };
    navigate('/blog/new', { state: { mediaData: blogData } });
  };

  const handleToggleWatchlist = () => {
    dispatch(toggleShowWatchlist(showDetails.id));
  };

  return (
    <Layout $backdrop={showDetails.backdrop_path ? true : false}>
      <SEOHead
        title={seoTitle}
        description={showDetails.overview || t('media.noDescription', 'No description available')}
        image={`https://image.tmdb.org/t/p/original${showDetails.backdrop_path}`}
        type="tv"
        structuredData={structuredData as unknown as Record<string, any>}
      />
      {/* PublicitÃƒÂ© HighPerformance en haut */}
      <HighPerformanceBanner 
        adKey="2154f7f7df439957dfc24240cd37dd3c"
        width={728}
        height={90}
        id="hp-banner-tv-top"
        hideOnMobile
      />
      
      {/* PublicitÃƒÂ© HilltopAds */}
      <HilltopAd id="hilltop-tv-1" />
      
      <DetailContainer>
        <BackdropImage backdrop={showDetails.backdrop_path} />
        <Content>
          <PosterContainer>
            <img
              src={`https://image.tmdb.org/t/p/w500${showDetails.poster_path}`}
              alt={showDetails.name}
            />
          </PosterContainer>

          <InfoContainer>
            <Title>{showDetails.name}</Title>

            {showDetails.genres && (
              <GenresContainer>
                {showDetails.genres.map((genre) => (
                  <GenreTag key={genre.id} to={`/browse?genre=${genre.id}`}>
                    <Tag size={16} />
                    {genre.name}
                  </GenreTag>
                ))}
              </GenresContainer>
            )}

            <MetaInfo>
              <MetaItem>
                <Star size={20} />
                {showDetails.vote_average.toFixed(1)}/10
              </MetaItem>
              <MetaItem>
                <Calendar size={20} />
                {firstAirYear}
              </MetaItem>
              <MetaItem>
                {t('media.seasonCount', { count: showDetails.number_of_seasons })}
              </MetaItem>
            </MetaInfo>

            <Overview>
              {showDetails.overview || t('media.noDescription', 'No description available')}
            </Overview>

            

            <ActionButtons>
              {isAdmin && (
                <Button
                  icon={<PenSquare size={20} />}
                  onClick={handleCreateBlogPost}
                  variant="secondary"
                >
                  {t('blog.createPost', 'Create Blog Post')}
                </Button>
              )}
            </ActionButtons>

            {/* Bandes-annonces et extraits */}
            {showDetails.videos?.results && (
              <VideoSection 
                videos={showDetails.videos.results}
                mediaId={showDetails.id}
                mediaType="tv"
                mediaTitle={showDetails.name}
              />
            )}

            {/* Distribution */}
            {showDetails.credits?.cast && (
              <CastSection cast={showDetails.credits.cast} />
            )}

            <StreamingServices id={showDetails.id} type="tv" />

            {/* Saisons */}
            {showDetails.seasons && (
              <SeasonSection seasons={showDetails.seasons} tvId={showDetails.id} />
            )}
          </InfoContainer>
        </Content>
      </DetailContainer>
      
      {/* PublicitÃƒÂ© HighPerformance en bas */}
      <HighPerformanceBanner 
        adKey="2154f7f7df439957dfc24240cd37dd3c"
        width={728}
        height={90}
        id="hp-banner-tv-bottom"
      />

      {/* CrackRevenue Postitial (desktop) / Fallback overlay (mobile) */}
      <Crxcr2PopIn 
        url="https://t.vlmai-3.com/408074/10046/0?aff_sub5=SF_006OG000004lmDN&aff_sub4=AT_0016"
        contentUrl="https://www.gptgirlfriend.online/?aff_id=1&transaction_id=postitial"
        postitialBehavior
        expireDays={0.01}
        forceFallback={forceMobileFallback}
      />
    </Layout>
  );
};

export default TVShowDetail;



